import React, { useState } from 'react';
import classnames from 'classnames';
import { Transition } from 'react-transition-group';

import './Collapse.scss';

export interface CollapseProps {
    /**
     * Tag
     */
    tag?: any;
    /**
     * Is opened collapse
     */
    opened?: boolean;
    /**
     * Appear Transtion
     */
    appear?: boolean;
    /**
     * Enter Transtion
     */
    enter?: boolean;
    /**
     * Exit Transtion
     */
    exit?: boolean;
    /**
     * Timeout transtion
     */
    timeout?: number;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * onEntering function
     */
    onEntering?(node: any, isAppearing: any): any;
    /**
     * onEntered function
     */
    onEntered?(node: any, isAppearing: any): any;
    /**
     * onExit function
     */
    onExit?(node: any): any;
    /**
     * onExiting function
     */
    onExiting?(node: any): any;
    /**
     * onExited function
     */
    onExited?(node: any): any;
    /**
     * Slot component
     */
    children?: React.ReactNode;
}

export const TransitionStatuses = {
    ENTERING: 'entering',
    ENTERED: 'entered',
    EXITING: 'exiting',
    EXITED: 'exited',
};

const transitionStatusToClassHash = {
    [TransitionStatuses.ENTERING]: 'collapsing',
    [TransitionStatuses.ENTERED]: 'collapse show',
    [TransitionStatuses.EXITING]: 'collapsing',
    [TransitionStatuses.EXITED]: 'collapse',
};

const getTransitionClass = (status: any) => transitionStatusToClassHash[status] || 'collapse';

const getHeight = (node: any) => node.scrollHeight;

const Collapse: React.FC<CollapseProps> = ({
    tag: Tag = 'div',
    opened = false,
    appear = false,
    enter = true,
    exit = true,
    timeout = 3500,
    className,
    style = {},
    children,
    onEntering = () => {},
    onEntered = () => {},
    onExit = () => {},
    onExiting = () => {},
    onExited = () => {},
}) => {
    const [height, setHeight] = useState<number | null>(null);

    const handleOnEntering = (node: any, isAppearing: any) => {
        setHeight(getHeight(node));
        onEntering(node, isAppearing);
    };

    const handleOnEntered = (node: any, isAppearing: any) => {
        setHeight(null);
        onEntered(node, isAppearing);
    };

    const handleOnExit = (node: any) => {
        setHeight(getHeight(node));
        onExit(node);
    };

    const handleOnExiting = (node: any) => {
        setHeight(0);
        onExiting(node);
    };

    const handleOnExited = (node: any) => {
        setHeight(null);
        onExited(node);
    };

    const renderChildren = (status: any) => {
        const classNames = classnames([getTransitionClass(status), className]);
        return (
            <Tag className={classNames} style={{ ...style, ...(height === null ? {} : { height }) }}>
                {children}
            </Tag>
        );
    };

    return (
        <Transition
            in={opened}
            appear={appear}
            enter={enter}
            exit={exit}
            timeout={timeout}
            onEntering={handleOnEntering}
            onEntered={handleOnEntered}
            onExit={handleOnExit}
            onExiting={handleOnExiting}
            onExited={handleOnExited}
        >
            {renderChildren}
        </Transition>
    );
};

export default Collapse;
